import { c as _c } from "react/compiler-runtime";
import { Button } from 'platform/components';
import { useState } from 'react';
import { useFilterCategorySelection } from '../../../../hooks/useFilterCategorySelection';
import type { EagColumnOrGroup } from '../../../../types/EagColumnGroup';
import type { FilterState } from '../../../../types/FilterState';
import { FilterContentArea } from '../FilterContentArea/FilterContentArea';
import { FilterCategoryMenu } from './components/FilterCategoryMenu/FilterCategoryMenu';
type FilterPanelProps = {
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
};

/**
 * Main filter panel for the desktop 75vw slide-in filter UI.
 *
 * Layout:
 * ```
 * ┌──────────────────────────────────────────────────────┐
 * │ ┌──────────────────────┬──────────────────────────┐  │
 * │ │ [Search...]          │  Content (scrollable)    │  │
 * │ │ Category list        │  - 2-column grid         │  │
 * │ │  ● General      (2)  │                          │  │
 * │ │  ● Pricing           │                          │  │
 * │ │                      │                          │  │
 * │ │ [Reset filters]      │                          │  │
 * │ └──────────────────────┴──────────────────────────┘  │
 * └──────────────────────────────────────────────────────┘
 * ```
 *
 * The left column uses a custom searchable category list (`FilterCategoryMenu`)
 * that supports cross-category search — typing a filter's name will surface the
 * parent category even if the category label itself doesn't match the query.
 *
 * ## State
 * - `selectedCategoryId` — managed by `useFilterCategorySelection`, auto-set to
 *   first visible category on mount and re-corrected when search filters it out
 * - `searchTerm` — controlled by this component, forwarded to both the category
 *   menu (for filtering the category list) and the content area (for filtering
 *   the filter widgets within the selected category)
 *
 * ## Why props, not store reads
 *
 * `filterState`, `onFilterChange`, and `onResetFilters` are stable callbacks
 * injected from `EagGridTable` via `Sidebar`.  This keeps the panel
 * decoupled from the store and trivially testable without a provider.
 */
export function FilterPanel(props) {
  const $ = _c(24);
  const [searchTerm, setSearchTerm] = useState("");
  let t0;
  if ($[0] !== props.columnConfig || $[1] !== props.filterState || $[2] !== searchTerm) {
    t0 = {
      columnConfig: props.columnConfig,
      filterState: props.filterState,
      searchTerm
    };
    $[0] = props.columnConfig;
    $[1] = props.filterState;
    $[2] = searchTerm;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const {
    selectedCategoryId,
    setSelectedCategoryId
  } = useFilterCategorySelection(t0);
  let t1;
  if ($[4] !== props.columnConfig || $[5] !== props.filterState || $[6] !== searchTerm || $[7] !== selectedCategoryId || $[8] !== setSelectedCategoryId) {
    t1 = <div className="flex-1 overflow-hidden"><FilterCategoryMenu columnConfig={props.columnConfig} filterState={props.filterState} selectedCategoryId={selectedCategoryId} onCategorySelect={setSelectedCategoryId} searchTerm={searchTerm} onSearchChange={setSearchTerm} /></div>;
    $[4] = props.columnConfig;
    $[5] = props.filterState;
    $[6] = searchTerm;
    $[7] = selectedCategoryId;
    $[8] = setSelectedCategoryId;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== props.onResetFilters) {
    t2 = <div className="flex-shrink-0 px-4 py-4 border-t border-gray-100"><Button variant="secondary" isFullWidth={true} onClick={props.onResetFilters} title="Reset filters" /></div>;
    $[10] = props.onResetFilters;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== t1 || $[13] !== t2) {
    t3 = <div className="w-56 flex-shrink-0 border-r border-gray-100 flex flex-col overflow-hidden">{t1}{t2}</div>;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  let t4;
  if ($[15] !== props.columnConfig || $[16] !== props.filterState || $[17] !== props.onFilterChange || $[18] !== searchTerm || $[19] !== selectedCategoryId) {
    t4 = <div className="flex-1 overflow-hidden"><FilterContentArea columnConfig={props.columnConfig} selectedCategoryId={selectedCategoryId} filterState={props.filterState} onFilterChange={props.onFilterChange} searchTerm={searchTerm} /></div>;
    $[15] = props.columnConfig;
    $[16] = props.filterState;
    $[17] = props.onFilterChange;
    $[18] = searchTerm;
    $[19] = selectedCategoryId;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== t3 || $[22] !== t4) {
    t5 = <div className="flex h-full overflow-hidden">{t3}{t4}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  return t5;
}